/**
 * What: The "talk to the TestFeed team" block. `large` is the Settings block (question, text, two buttons,
 *   photo); `compact` fits the Study Builder sidebar (photo, one line, one link). Both use the same rounded-square
 *   photo and the same `base` photo-to-text gap, so the two always read as one thing.
 * Transport / data-flow: Pure presentation; callers own the booking and support URLs.
 *
 * Follows Shopify's callout-card composition pattern (shopify.dev/docs/api/app-home/latest/patterns/
 * compositions/callout-card) for `large`. The pattern's dismiss button is deliberately dropped: a settings
 * page's only path to support shouldn't be something that can be permanently dismissed.
 */
import { shopifyComponentCopy } from '@testfeed/product-language'
import { houseImagery } from './imagery/houseImagery'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../shopifyUiTracking'

type TestReviewHelpProps =
  | { variant: 'large'; bookingUrl: string; supportUrl: string }
  | { variant: 'compact'; bookingUrl: string }

function Photo({ size }: { size: '48px' | '96px' }) {
  return (
    <s-box maxInlineSize={size} borderRadius="large-200" overflow="hidden">
      <s-image
        src={houseImagery.supportContact}
        alt={shopifyComponentCopy.houseImagery.supportContactAlt}
        aspectRatio="1"
        objectFit="cover"
      />
    </s-box>
  )
}

export function TestReviewHelp(props: TestReviewHelpProps) {
  const shopify = useAppBridge()
  if (props.variant === 'compact') {
    return (
      <s-stack direction="inline" gap="base" alignItems="center">
        <Photo size="48px" />
        <s-stack direction="block" gap="small-300">
          <s-text color="subdued">{shopifyComponentCopy.studyBuilderPage.helpHeading}</s-text>
          <s-link
            href={props.bookingUrl}
            target="_blank"
            onClick={() => trackShopifyAction(shopify, 'open-booking', () => undefined)}
          >
            {shopifyComponentCopy.studyBuilderPage.bookHelp}
          </s-link>
        </s-stack>
      </s-stack>
    )
  }
  const copy = shopifyComponentCopy.settingsPage
  return (
    <s-grid gridTemplateColumns="@container (inline-size <= 480px) 1fr, 1fr auto" gap="base" alignItems="center">
      <s-stack direction="block" gap="small-200">
        <s-heading>{copy.needHelpConfiguringYourTestfeed006}</s-heading>
        <s-paragraph color="subdued">{copy.checkOutOurDocumentationTo007}</s-paragraph>
        <s-stack direction="inline" gap="base">
          <s-button
            href={props.bookingUrl}
            target="_blank"
            variant="primary"
            onClick={() => trackShopifyAction(shopify, 'open-booking', () => undefined)}
          >
            {copy.bookStrategyCall010}
          </s-button>
          <s-button
            href={props.supportUrl}
            variant="tertiary"
            onClick={() => trackShopifyAction(shopify, 'open-support', () => undefined)}
          >
            {copy.contactSupport009}
          </s-button>
        </s-stack>
      </s-stack>
      <Photo size="96px" />
    </s-grid>
  )
}
